import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    List, 
    ListItem, 
    ListItemText, 
    ListItemIcon,
    CircularProgress,
    Divider
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const DocumentationPage = () => {
    const [docsList, setDocsList] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [docContent, setDocContent] = useState('');
    const [loadingList, setLoadingList] = useState(true);
    const [loadingContent, setLoadingContent] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchDocsList();
    }, []);

    const fetchDocsList = async () => {
        try {
            const response = await axios.get(`${API_URL}/docs/list`, { headers: getAuthHeaders() });
            setDocsList(response.data);
            if (response.data.length > 0) {
                // Auto-select first document
                handleSelectDoc(response.data[0]);
            }
        } catch (err) {
            console.error("Error fetching docs list:", err);
            setError('Não foi possível carregar a lista de documentos. Verifique suas permissões de administrador.');
        } finally {
            setLoadingList(false);
        }
    };

    const handleSelectDoc = async (doc) => {
        setSelectedDoc(doc);
        setLoadingContent(true);
        setError('');
        try {
            const response = await axios.get(`${API_URL}/docs/file?name=${doc.filename}`, { headers: getAuthHeaders() });
            setDocContent(response.data.content);
        } catch (err) {
            console.error("Error fetching doc content:", err);
            setError('Não foi possível carregar o conteúdo do documento.');
            setDocContent('');
        } finally {
            setLoadingContent(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', gap: 3, p: 2 }}>
            {/* Sidebar with Document List */}
            <Paper 
                elevation={3} 
                sx={{ 
                    width: '300px', 
                    minWidth: '300px',
                    flexShrink: 0,
                    height: '100%', 
                    overflowY: 'auto',
                    backgroundColor: 'background.paper',
                    borderRadius: 2
                }}
            >
                <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MenuBookIcon /> Documentação
                    </Typography>
                </Box>
                <Divider />
                
                {loadingList ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List component="nav">
                        {docsList.map((doc) => (
                            <ListItem 
                                button 
                                key={doc.filename}
                                selected={selectedDoc && selectedDoc.filename === doc.filename}
                                onClick={() => handleSelectDoc(doc)}
                                sx={{
                                    '&.Mui-selected': {
                                        backgroundColor: 'action.selected',
                                        borderLeft: '4px solid',
                                        borderColor: 'primary.main'
                                    }
                                }}
                            >
                                <ListItemIcon>
                                    <DescriptionIcon color={selectedDoc && selectedDoc.filename === doc.filename ? 'primary' : 'inherit'} />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={doc.title} 
                                    primaryTypographyProps={{ 
                                        fontWeight: selectedDoc && selectedDoc.filename === doc.filename ? 'bold' : 'normal',
                                        fontSize: '0.9rem'
                                    }} 
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Paper>

            {/* Main Content Area */}
            <Paper 
                elevation={3} 
                sx={{ 
                    flexGrow: 1, 
                    height: '100%', 
                    overflowY: 'auto',
                    p: 4,
                    backgroundColor: 'background.paper',
                    borderRadius: 2
                }}
            >
                {error && (
                    <Typography color="error" variant="body1" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {loadingContent ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box 
                        className="markdown-body"
                        sx={{ 
                            '& h1': { borderBottom: '1px solid #eaecef', pb: 1, mt: 0 },
                            '& h2': { borderBottom: '1px solid #eaecef', pb: 1, mt: 4 },
                            '& code': { backgroundColor: 'action.hover', padding: '2px 4px', borderRadius: '4px', fontFamily: 'monospace' },
                            '& pre': { backgroundColor: '#1e1e1e', color: '#d4d4d4', padding: '16px', borderRadius: '8px', overflowX: 'auto' },
                            '& blockquote': { borderLeft: '4px solid', borderColor: 'primary.main', pl: 2, color: 'text.secondary', margin: '16px 0' },
                            '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                            '& th, & td': { border: '1px solid', borderColor: 'divider', padding: '8px 12px' },
                            '& th': { backgroundColor: 'action.hover' }
                        }}
                    >
                        {docContent ? (
                            <ReactMarkdown>{docContent}</ReactMarkdown>
                        ) : (
                            <Typography color="text.secondary">
                                Selecione um documento no menu à esquerda para visualizar.
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default DocumentationPage;
